'use client';

import { motion } from 'framer-motion';
import React, { useState, useEffect, useRef, Suspense } from 'react';
import { usePathname } from 'next/navigation';
import { List, X } from '@phosphor-icons/react';
import { UserIcon } from '@/components/icon/user';
import { CaretRight, CaretDown } from '@phosphor-icons/react';
import Link from 'next/link';
import UserInfoModal from '@/components/dashboard/userInfoModal';
import { getAuth } from 'firebase/auth';
import { useUserInfo } from '@/provider/UserInfoProvider';
import Image from 'next/image';
import { handleLogout } from '@/utils//authUtils';
import Breadcrumbs from '@/components/layout/Breadcrumbs';
import { app } from '@/config/firebaseConfig';
import { SaleIcon } from '@/components/icon/sale';
import useRoute from '@/ueshook/useRoute';
import PromoteHeader from './PromoteHeader';
import { useSignupModal } from '@/zustand/store';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useFeatureFlagVariantKey } from 'posthog-js/react';
import { PostHogABTest } from '@/posthog/abtest';
import { LAYOUT_DEFINE } from '@/constants';

interface Link {
  id: number;
  link: string;
  name: string;
}
const showBadge = true;
/**
 * @deprecated isAvailable 未来去除
 * @param showBadge 未上线为true
 */
const toolkitCategories = [
  {
    title: 'AI Removal Suite',
    items: [
      {
        name: 'Watermark Removal',
        href: '/watermark-removal',
        showBadge: false,
        isAvailable: true,
        isNew: false,
      },
      {
        name: 'Text Removal',
        href: '/remove-text-from-image',
        showBadge: false,
        isAvailable: true,
        isNew: false,
      },
      {
        name: 'Logo Removal',
        href: '/logo-remover',
        showBadge: false,
        isAvailable: true,
        isNew: false,
      },
      {
        name: 'Signature Removal',
        href: '/remove-signature',
        showBadge: false,
        isAvailable: true,
        isNew: false,
      },
      {
        name: 'Stamp Removal',
        href: '/photo-stamp-remover',
        showBadge: false,
        isAvailable: true,
        isNew: false,
      },
      {
        name: 'Sticker Removal',
        href: '/sticker-remover',
        showBadge: false,
        isAvailable: true,
        isNew: false,
      },
    ],
  },
  {
    title: 'Professional Image Generation',
    items: [
      {
        name: 'AI Headshot',
        href: '/ai-headshot-generator',
        showBadge: true,
        isAvailable: true,
        isNew: true,
      },
      {
        name: 'Image Expand',
        href: '/ai-expand',
        showBadge: true,
        isAvailable: true,
        isNew: true,
      },
    ],
  },
  {
    title: 'Image Restoration',
    items: [
      {
        name: 'Photo Restoration',
        href: '/photo-restoration',
        showBadge: true,
        isAvailable: true,
        isNew: true,
      },
      {
        name: 'Image Enhancer',
        href: '/image-enhancer',
        showBadge: false,
        isAvailable: true,
        isNew: false,
      },
      {
        name: 'Image Unblur',
        href: '/unblur-image',
        showBadge: false,
        isAvailable: true,
        isNew: false,
      },
      {
        name: 'Image Upscaler',
        href: '/image-upscaler',
        showBadge: false,
        isAvailable: true,
        isNew: false,
      },
    ],
  },
  {
    title: 'Background Removal',
    items: [
      {
        name: 'Background Remover',
        href: '/background-remover',
        showBadge: false,
        isAvailable: true,
        isNew: false,
      },
      {
        name: 'Png Maker',
        href: '/png-maker',
        showBadge: false,
        isAvailable: true,
        isNew: false,
      },
      {
        name: 'Transparent Background',
        href: '/transparent-background-maker',
        showBadge: false,
        isAvailable: true,
        isNew: false,
      },
      {
        name: 'White Background Maker',
        href: '/white-background-maker',
        showBadge: false,
        isAvailable: true,
        isNew: false,
      },
      {
        name: 'Black Background Maker',
        href: '/black-background-maker',
        showBadge: false,
        isAvailable: true,
        isNew: false,
      },
    ],
  },
];

const toolkitItems = toolkitCategories.flatMap((category) => category.items);

const links: Link[] = [
  {
    id: 1,
    link: '/pricing',
    name: 'Pricing',
  },
  {
    id: 2,
    link: '/api',
    name: 'API',
  },
  {
    id: 3,
    link: '/blog',
    name: 'Blog',
  },
];

function HeaderTop() {
  const { push } = useRoute();

  // 获取 AB test 配置
  const { bannerABTest } = PostHogABTest;

  const variant = useFeatureFlagVariantKey(bannerABTest.name);

  // 默认设置posthog，可移除
  // posthog.featureFlags.override({ [bannerABTest.name]: bannerABTest.bTest });
  // console.log('variant', variant);

  return variant === 'test' ? (
    <div
      className='flex h-full w-full cursor-pointer items-center justify-center text-white'
      style={{
        background:
          'linear-gradient(90deg,#ec0503 0%,#ff3d3c 30.96%,#9ac496 76.39%,#1d8b44 100%)',
      }}
      onClick={() => {
        push('/pricing');
      }}
    >
      <div className='flex flex-col items-center justify-center lg:hidden'>
        <span className='flex'>
          <SaleIcon /> kaze.ai New Year offers!
        </span>
        <span className='-tracking-[0.02rem]'>
          Lowest prices of the year: Annual Plans 50% off {'>'}
        </span>
      </div>

      <div className='hidden py-2 lg:flex'>
        <SaleIcon /> kaze.ai New Year offers! Lowest prices of the year: Annual
        Plans 50% off {'>'}
      </div>
    </div>
  ) : null;
}

const Header_tailwind_h = LAYOUT_DEFINE.HEADER_HEIGHT;
const header_height = `h-${Header_tailwind_h}`;

function Nav() {
  // 隐藏头部banner的页面
  const hideHeaderBannerPaths = ['/ai-headshot-generator/select-images'];
  const pathname = usePathname();
  const { push } = useRoute();
  const [mobileMenuOpen, setMobileMenuOpen] = useState<boolean>(false);
  const [isToolkitOpen, setIsToolkitOpen] = useState(false);
  const [isUserInfoModalOpen, setIsUserInfoModalOpen] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  const setSignupModalOpen = useSignupModal((s) => s.setSignupModalOpen);

  const handleClick = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  useEffect(() => {
    const handleRouteChange = () => {
      setMobileMenuOpen(false);
    };

    handleRouteChange();
  }, [pathname]);

  useEffect(() => {
    const unsubscribe = getAuth(app).onAuthStateChanged((user) => {
      if (user && !user.isAnonymous) {
        setUserId(user.uid);
      } else {
        setUserId(null);
      }
    });

    return () => unsubscribe();
  }, []);

  const handleLogoutWithCallbacks = async () => {
    // 退出登录瞬间清缓存
    typeof window !== 'undefined' && window.localStorage.clear();

    await handleLogout([() => setUserId(null)]);
  };

  const toLogin = () => {
    setSignupModalOpen(true);
  };

  return (
    <>
      <div className='header-container z-40'>
        {pathname.includes('watermark-removal') ? <PromoteHeader /> : <div />}
        <div
          className={`pointer-events-auto relative z-50 flex ${header_height} w-full shrink-0 flex-row items-center justify-between gap-6 px-8 py-5 lg:px-28`}
        >
          <div className='flex flex-row items-center gap-x-10'>
            <Image
              priority
              src='https://imagedelivery.net/7rExjEZEypWvglDwZ5-zxA/ffee010c-d40e-437a-e26f-ecd79eda1b00/public'
              alt='Kaze AI Logo'
              className='z-50 h-[1.875rem] w-[6.25rem] cursor-pointer'
              width={100}
              height={30}
              onClick={() => push('/')}
            />
            <div className='hidden items-center gap-x-7 md:flex'>
              <div className='relative'>
                <div
                  onMouseEnter={() => setIsToolkitOpen(true)}
                  className='flex h-fit cursor-pointer items-center gap-1 border-b-2 border-transparent py-1 text-base font-medium text-gray-800 transition duration-200 hover:border-stone-950'
                >
                  Toolkit
                  <CaretDown className='ml-1 h-4 w-4' />
                </div>
                {isToolkitOpen && (
                  <div
                    className='absolute left-0 mt-6 w-[600px] rounded-md bg-white p-8 shadow-lg ring-1 ring-black ring-opacity-5 backdrop-blur-md'
                    onMouseEnter={() => setIsToolkitOpen(true)}
                    onMouseLeave={() => setIsToolkitOpen(false)}
                  >
                    <div
                      className='flex flex-col space-y-4'
                      role='menu'
                      aria-orientation='vertical'
                      aria-labelledby='options-menu'
                    >
                      {toolkitCategories.map((category, categoryIndex) => (
                        <React.Fragment key={category.title}>
                          <div className='mb-1'>
                            <div className='mb-2 text-lg font-semibold text-black'>
                              {category.title}
                            </div>
                            <div className='grid grid-cols-2 gap-x-24'>
                              {category.items.map((item) => (
                                <Link
                                  key={item.name}
                                  href={item.isAvailable ? item.href : '#'}
                                  className='block pb-2 pr-3'
                                >
                                  <p className='inline-block pt-1 text-base font-medium text-black hover:underline hover:underline-offset-2'>
                                    {item.name}
                                  </p>
                                  {item.showBadge && showBadge && (
                                    <>
                                      {!item.isAvailable && (
                                        <Badge
                                          variant='secondary'
                                          className='ml-2 inline-block rounded-full'
                                        >
                                          Coming Soon
                                        </Badge>
                                      )}
                                      {item.isNew && item.isAvailable && (
                                        <Badge
                                          variant='destructive'
                                          className='ml-2 inline-block rounded-full bg-gradient-to-r from-[#E83273] to-[#E43240]'
                                        >
                                          New
                                        </Badge>
                                      )}
                                    </>
                                  )}
                                </Link>
                              ))}
                            </div>
                          </div>
                          {categoryIndex < toolkitCategories.length - 1 && (
                            <Separator className='my-1' />
                          )}
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              {links.map(({ link, name }, idx) => (
                <LinkButton
                  key={idx}
                  link={link}
                  name={name}
                  isActive={pathname === link}
                />
              ))}
            </div>
          </div>
          <div className='flex'>
            <PrimaryButton setIsUserInfoModalOpen={setIsUserInfoModalOpen} />
            <button
              className='bg-white-100 flex h-[29px] w-[29px] items-center justify-center rounded-full bg-opacity-90 md:hidden'
              onClick={handleClick}
              type='button'
            >
              {!mobileMenuOpen && (
                <List size={20} color='black' className='ml-px' />
              )}
              {mobileMenuOpen && <X size={20} color='black' />}
            </button>
          </div>

          {/* 移动端菜单 - 直接在header下方展开 */}
          {mobileMenuOpen && (
            <div
              className={`fixed left-0 right-0 ${pathname.includes('watermark-removal') ? 'top-[40px]' : 'top-0'} z-50 flex h-screen w-full flex-col overflow-hidden bg-white md:hidden`}
            >
              {/* 菜单顶部导航条 */}
              <div className='border-b border-gray-200'>
                <div className='flex h-[70px] items-center justify-between px-8'>
                  <div>
                    <Image
                      src='https://imagedelivery.net/7rExjEZEypWvglDwZ5-zxA/ffee010c-d40e-437a-e26f-ecd79eda1b00/public'
                      alt='Kaze AI Logo'
                      className='h-[1.875rem] w-[6.25rem] cursor-pointer'
                      width={100}
                      height={30}
                      onClick={() => {
                        push('/');
                        setMobileMenuOpen(false);
                      }}
                    />
                  </div>
                  <button
                    className='flex h-[29px] w-[29px] items-center justify-center rounded-full'
                    onClick={() => setMobileMenuOpen(false)}
                    type='button'
                  >
                    <X size={20} color='black' />
                  </button>
                </div>
              </div>

              {/* 可滚动的内容区域 */}
              <div className='flex-1 overflow-y-auto px-6 [&::-webkit-scrollbar]:hidden'>
                <div className='divide-y divide-gray-500/10'>
                  <div className='space-y-2 py-6'>
                    <div className='relative'>
                      <motion.button
                        onClick={() => setIsToolkitOpen(!isToolkitOpen)}
                        className='flex w-full flex-row items-center justify-between rounded-lg px-3 py-2.5 text-left text-xl font-medium leading-7 text-gray-900 hover:bg-gray-50'
                      >
                        Toolkit
                        <CaretRight
                          className={`ml-1 h-5 w-5 ${isToolkitOpen ? 'rotate-90' : ''} transition-transform`}
                          size={28}
                        />
                      </motion.button>
                      <Separator className='' />
                      {isToolkitOpen && (
                        <div className='mt-5 pl-4'>
                          {toolkitCategories.map((category, categoryIndex) => (
                            <React.Fragment key={category.title}>
                              <div className='mb-4'>
                                <div className='mb-2 text-lg font-semibold text-gray-800'>
                                  {category.title}
                                </div>
                                <div className='grid grid-cols-1 gap-x-2'>
                                  {category.items.map((item) => (
                                    <Link
                                      key={item.name}
                                      href={item.isAvailable ? item.href : '#'}
                                      className='block py-1 text-base text-black'
                                      onClick={() => setMobileMenuOpen(false)}
                                    >
                                      {item.name}
                                      {item.showBadge && showBadge && (
                                        <>
                                          {!item.isAvailable && (
                                            <Badge
                                              variant='secondary'
                                              className='ml-2 inline-block rounded-full'
                                            >
                                              Coming Soon
                                            </Badge>
                                          )}
                                          {item.isNew && item.isAvailable && (
                                            <Badge
                                              variant='destructive'
                                              className='ml-2 inline-block rounded-full bg-gradient-to-r from-[#E83273] to-[#E43240]'
                                            >
                                              New
                                            </Badge>
                                          )}
                                        </>
                                      )}
                                    </Link>
                                  ))}
                                </div>
                              </div>
                            </React.Fragment>
                          ))}
                        </div>
                      )}
                    </div>
                    {links.map(({ link, name }, idx) => (
                      <React.Fragment key={idx}>
                        <span
                          className='block rounded-lg px-3 py-2.5 text-xl font-medium leading-7 text-gray-900 hover:bg-gray-50'
                          onClick={() => {
                            push(link);
                            setMobileMenuOpen(false);
                          }}
                        >
                          {name}
                        </span>
                        <Separator className='' />
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              </div>

              {/* 固定在底部的按钮区域 */}
              <div
                className={`sticky bottom-0 border-t border-gray-200 bg-white ${
                  pathname.includes('watermark-removal') ? 'pb-20' : 'pb-16'
                }`}
              >
                <div className='flex min-h-[180px] flex-col gap-2 px-6 pb-[calc(1rem+env(safe-area-inset-bottom))] pt-4'>
                  {userId ? (
                    <div className='flex h-fit flex-col gap-2'>
                      <Button
                        className='w-full rounded-md text-[#FAFAFA]'
                        onClick={() => {
                          push('/my-projects');
                          setMobileMenuOpen(false);
                        }}
                      >
                        My Projects
                      </Button>
                      <Button
                        className='w-full rounded-md text-[#FAFAFA]'
                        onClick={() => {
                          setIsUserInfoModalOpen(true);
                          setMobileMenuOpen(false);
                        }}
                      >
                        Account
                      </Button>
                      <Button
                        variant='outline'
                        className='w-full rounded-md'
                        onClick={handleLogoutWithCallbacks}
                      >
                        Log Out
                      </Button>
                    </div>
                  ) : (
                    <div className='flex h-fit flex-col gap-2'>
                      <Button
                        className='w-full'
                        onClick={() => {
                          toLogin();
                          setMobileMenuOpen(false);
                        }}
                      >
                        Try Kaze free
                      </Button>
                      <Button
                        variant='secondary'
                        className='secondary-button w-full'
                        onClick={() => {
                          toLogin();
                          setMobileMenuOpen(false);
                        }}
                      >
                        Log in
                      </Button>
                      {/* 添加一个隐藏的占位按钮，保持与登录状态相同的高度 */}
                      <div className='h-[38px]'></div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
        <Breadcrumbs
          className={`${pathname === '/' ? 'hidden' : 'block'} md:block`}
        />
      </div>
      <UserInfoModal
        isOpen={isUserInfoModalOpen}
        onClose={() => setIsUserInfoModalOpen(false)}
      />
    </>
  );
}

interface LinkButtonProps {
  link: string;
  name: string;
  isActive: boolean;
}

function LinkButton({ link, name }: LinkButtonProps) {
  return (
    <Link
      className='h-fit border-b-2 border-transparent py-1 text-base font-medium text-gray-800 transition duration-200 hover:border-stone-950'
      href={link}
    >
      {name}
    </Link>
  );
}

interface PrimaryButtonProps {
  setIsUserInfoModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

function PrimaryButton({ setIsUserInfoModalOpen }: PrimaryButtonProps) {
  const setSignupModalOpen = useSignupModal((s) => s.setSignupModalOpen);
  const { push } = useRoute();
  const [isDashboardOpen, setIsDashboardOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const { userInfo, isLoadingUser, userIdentity } = useUserInfo();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { setUserIdentity } = useUserInfo();

  useEffect(() => {
    setMounted(true);
  }, []);

  const toLogin = () => setSignupModalOpen(true);

  const handleLogoutWithCallbacks = async () => {
    await handleLogout([
      () => {
        // 退出登录瞬间清缓存
        typeof window !== 'undefined' && window.localStorage.clear();
      },
      () => setIsDashboardOpen(false),
      () => setUserIdentity(1),
    ]);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setIsDashboardOpen(false);
    }, 1000);
  };

  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  if (!mounted) return null; // Only this button disappears during hydration

  return (
    <div className='hidden flex-row items-center gap-4 md:flex'>
      {userIdentity === 3 ? (
        <div
          className='relative pr-36'
          onMouseLeave={handleMouseLeave}
          onMouseEnter={handleMouseEnter}
        >
          <button
            className={`rounded-full border border-gray-200 bg-gray-300/10 p-1.5 backdrop-blur-xl`}
            onClick={(e) => {
              e.stopPropagation();
              setIsDashboardOpen(!isDashboardOpen);
            }}
          >
            <UserIcon />
          </button>
          {isDashboardOpen && (
            <div
              className='absolute left-0 top-12 flex w-56 flex-col gap-2 rounded-md bg-gray-100 bg-opacity-90 p-4 text-left shadow-lg ring-1 ring-black ring-opacity-5 backdrop-blur-xl'
              onClick={(e) => e.stopPropagation()}
              onMouseLeave={() => setIsDashboardOpen(false)}
            >
              {isLoadingUser ? (
                <p className='text-black'>Loading user info...</p>
              ) : userInfo ? (
                <>
                  <p
                    className={`w-fit text-xs ${
                      userInfo.subscription.subscription_name === 'free'
                        ? 'text-green-500'
                        : 'bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent'
                    }`}
                  >
                    {userInfo.subscription.subscription_name}
                  </p>
                  <p className='w-full bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-2xl font-semibold text-transparent'>
                    {userInfo.plan.available_count} Credits
                  </p>
                  <Link
                    href={'/pricing'}
                    onClick={(e) => {
                      e.preventDefault();
                      push('/pricing');
                    }}
                  >
                    <p className='text-sm font-medium text-gray-900 underline'>
                      Buy Credits or Upgrade
                    </p>
                  </Link>
                  <Separator className='my-2' />
                  <Button
                    className='w-full'
                    onClick={() => {
                      push('/my-projects');
                      setIsDashboardOpen(false);
                    }}
                  >
                    <p className='w-full'>My Projects</p>
                  </Button>
                  <Button
                    className='w-full'
                    onClick={() => {
                      setIsUserInfoModalOpen(true);
                      setIsDashboardOpen(false);
                    }}
                  >
                    <p className='w-full'>Account</p>
                  </Button>
                  <Button
                    variant='outline'
                    className='w-full'
                    onClick={handleLogoutWithCallbacks}
                  >
                    Log Out
                  </Button>
                </>
              ) : (
                <p className='text-black'>Loading user info...</p>
              )}
            </div>
          )}
        </div>
      ) : (
        <>
          <div
            onClick={toLogin}
            className='flex h-fit cursor-pointer items-center gap-1 border-b-2 border-transparent py-1 text-base font-medium text-gray-800 transition duration-200 hover:border-stone-950'
          >
            Log in
          </div>
          <Button className='rounded-md text-sm font-medium' onClick={toLogin}>
            Try Kaze free
          </Button>
        </>
      )}
    </div>
  );
}

export default function Header() {
  return (
    <Suspense fallback={<div>Loading Header...</div>}>
      <Nav />
    </Suspense>
  );
}
